/**
 * Friendly, localized product name for the patient-facing pages.
 *
 * get_patient_public_view returns `product_name = coalesce(products.name_en,
 * line_type::text)`. Custom lines have no products row, so it falls back to the
 * raw enum (e.g. "custom_insole"), which we must not show the patient. Map the
 * known line_type enums to a localized label; real marketplace product names
 * (which are never enum strings) pass through unchanged.
 */

type TranslateFn = (key: string, defaultValue: string) => string;

const LINE_TYPE_LABELS: Record<string, { key: string; fallback: string }> = {
  custom_insole: { key: 'product_name_custom_insole', fallback: 'Custom insole' },
  premium_3d_insole: { key: 'product_name_premium_3d_insole', fallback: '3D-printed insole' },
  orthopedic_sandal: { key: 'product_name_orthopedic_sandal', fallback: 'Orthopaedic sandal' },
  sandal_insole_combo: { key: 'product_name_sandal_combo', fallback: 'Sandal with insole' },
  ots_insole: { key: 'product_name_ots_insole', fallback: 'Ready-made insole' },
  other_equipment: { key: 'product_name_other_equipment', fallback: 'Orthopaedic equipment' },
  orthopaedic_material: {
    key: 'product_name_orthopaedic_material',
    fallback: 'Orthopaedic materials',
  },
};

export function patientProductName(
  rawName: string | null | undefined,
  t: TranslateFn,
): string {
  if (!rawName) return t('product_label', 'Your insole');
  const entry = LINE_TYPE_LABELS[rawName];
  return entry ? t(entry.key, entry.fallback) : rawName;
}
